import React, { useState, useRef } from "react"; 
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, Trash, MoreVertical } from "lucide-react";

const ExpenseHeadList = () => {
  // Dummy data for expense heads
  const [expenseHeads, setExpenseHeads] = useState([
    { id: 1, name: "Electric Bill", description: "Monthly electricity bill" },
    { id: 2, name: "Stationary Bill", description: "Office stationary expenses" },
    { id: 3, name: "Telephone Bill", description: "Monthly telephone bill" },
    { id: 4, name: "Rent", description: "Office rent payment" },
    { id: 5, name: "Salaries", description: "Employee salaries" },
  ]);

  const [totalRecords, setTotalRecords] = useState(expenseHeads.length);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(5);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedExpenseHead, setSelectedExpenseHead] = useState(null);
  const [expenseHeadToDelete, setExpenseHeadToDelete] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const toast = useRef(null);

  // Toggle dropdown
  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedExpenseHead) {
        // Update expense head
        const updatedExpenseHeads = expenseHeads.map((head) =>
          head.id === selectedExpenseHead.id ? { ...head, ...formData } : head
        );
        setExpenseHeads(updatedExpenseHeads);
        toast.current.show({
          severity: "success",
          summary: "Updated",
          detail: "Expense head updated successfully",
          life: 3000,
        });
      } else {
        // Add new expense head
        const newExpenseHead = { id: expenseHeads.length + 1, ...formData };
        setExpenseHeads([...expenseHeads, newExpenseHead]);
        setTotalRecords(expenseHeads.length + 1);
        toast.current.show({
          severity: "success",
          summary: "Added",
          detail: "Expense head added successfully",
          life: 3000,
        });
      }
      toggle();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save expense head",
        life: 3000,
      });
    }
    setLoading(false);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!expenseHeadToDelete) return;
    setLoading(true);
    try {
      const updatedExpenseHeads = expenseHeads.filter(
        (head) => head.id !== expenseHeadToDelete.id
      );
      setExpenseHeads(updatedExpenseHeads);
      setTotalRecords(updatedExpenseHeads.length);
      toast.current.show({
        severity: "success",
        summary: "Deleted",
        detail: "Expense head deleted successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete expense head",
        life: 3000,
      });
    }
    setExpenseHeadToDelete(null);
    setLoading(false);
    toggleDeleteModal();
  };

  // Toggle modal
  const toggle = () => {
    if (modal) {
      setSelectedExpenseHead(null);
      setFormData({
        name: "",
        description: "",
      });
    }
    setModal(!modal);
  };

  // Toggle delete modal
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  // Handle pagination
  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { title: "Finance", link: "/" },
    { title: "Expense Head List", link: "#" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Expense Head List" breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <span>Expense Head List</span>
              <Button color="primary" onClick={toggle}>
                <i className="mdi mdi-plus mr-1"></i> Add Expense Head
              </Button>
            </CardHeader>
            <CardBody>
              <DataTable
                value={expenseHeads.slice(first, first + rows)}
                loading={loading}
                stripedRows
              >
                {/* SI No Column */}
                <Column
                  header="SI No"
                  body={(rowData, { rowIndex }) => first + rowIndex + 1}
                />
                <Column field="name" header="Expense Head" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  header="Actions"
                  body={(rowData) => (
                    <Dropdown
                      isOpen={dropdownOpen === rowData.id}
                      toggle={() => toggleDropdown(rowData.id)}
                    >
                      <DropdownToggle tag="button" className="btn btn-light">
                        <MoreVertical size={20} />
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem
                          onClick={() => {
                            setSelectedExpenseHead(rowData);
                            setFormData({
                              name: rowData.name,
                              description: rowData.description,
                            });
                            toggle();
                          }}
                        >
                          <Edit size={16} className="me-2" />
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => {
                            setExpenseHeadToDelete(rowData);
                            toggleDeleteModal();
                          }}
                          className="text-danger"
                        >
                          <Trash size={16} className="me-2" />
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                />
              </DataTable>
              <Paginator
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPageChange={onPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Add/Edit Expense Head Modal */}
      <Modal isOpen={modal} toggle={toggle} centered>
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={toggle}>
            {selectedExpenseHead ? "Edit Expense Head" : "Add Expense Head"}
            {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label>Expense Head</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {selectedExpenseHead ? "Updating..." : "Adding..."}
                </>
              ) : (
                selectedExpenseHead ? "Update" : "Add"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this expense head?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={loading}>
            Delete
            {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
          </Button>
        </ModalFooter>
      </Modal>

      <Toast ref={toast} />
    </React.Fragment>
  );
};

export default ExpenseHeadList;